import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', ...(token ? { 'x-worker-token': token } : {}), ...opts.headers } })
    .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || `Request failed (${r.status})`); return d; });
}

// Worker time-off request sheet (new request + history). Lives with the time clock.
// `centered` = the desktop look (a dialog in the middle); the default is the phone's
// sheet rising from the bottom.
export default function TimeOffModal({ onClose, centered = false }) {
  const [form, setForm] = useState({ start_date: '', end_date: '', reason: '', kind: 'vacation' });
  // Work from home is for the office (Ryan, Nov 2 2026) — the field and shop never see the pill.
  const role = (() => { try { return (JSON.parse(localStorage.getItem('te_worker') || 'null') || {}).role || ''; } catch { return ''; } })();
  const officeSide = ['Executive', 'Accounting', 'OfficeStaff', 'ProjectManager', 'Admin', 'admin'].includes(role);
  const KINDS = [['vacation', '🌴 Vacation'], ['sick', '🤒 Sick'], ['personal', '🏠 Personal'], ...(officeSide ? [['wfh', '🏡 Work from home']] : []), ['other', '📝 Other']];
  const instant = k => k === 'sick' || k === 'wfh';   // recorded straight away, no approval
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const [myRequests, setMyRequests] = useState([]);
  const [tab, setTab] = useState('new');

  useEffect(() => { apiFetch('/my-time-off').then(setMyRequests).catch(() => {}); }, []);

  async function submit() {
    if (!form.start_date || !form.end_date) return setErr('Please select start and end dates');
    if (form.end_date < form.start_date) return setErr('End date must be on or after start date');
    setSubmitting(true); setErr('');
    try {
      const r = await apiFetch('/time-off', { method: 'POST', body: JSON.stringify(form) });
      setDone(r || true);
      apiFetch('/my-time-off').then(setMyRequests).catch(() => {});
    } catch (e) { setErr(e.message); }
    finally { setSubmitting(false); }
  }

  const statusStyle = s => ({
    pending:  { background: '#fef3c7', color: '#92400e' },
    approved: { background: '#d1fae5', color: '#065f46' },
    denied:   { background: '#fee2e2', color: '#991b1b' },
  }[s] || {});

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: centered ? 'center' : 'flex-end', justifyContent: 'center', padding: centered ? 16 : 0 }}>
      <div style={{ background: '#fff', borderRadius: centered ? 16 : '20px 20px 0 0', width: '100%', maxWidth: 480, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#111' }}>Time Off Request</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, color: '#999', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'flex', borderBottom: '1px solid #f0f0f0' }}>
          {['new', 'history'].map(t => (
            <button key={t} onClick={() => { setTab(t); setDone(false); }}
              style={{ flex: 1, padding: '10px 0', fontSize: 13, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', color: tab === t ? '#e87722' : '#9ca3af', borderBottom: tab === t ? '2px solid #e87722' : '2px solid transparent' }}>
              {t === 'new' ? 'New Request' : 'My Requests'}
            </button>
          ))}
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: 20 }}>
          {tab === 'new' && (
            done ? (
              <div style={{ textAlign: 'center', padding: '30px 0' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#065f46', marginBottom: 8 }}>{form.kind === 'sick' ? 'Sick day logged' : form.kind === 'wfh' ? 'Working from home — noted' : 'Request Submitted'}</div>
                <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 24 }}>{form.kind === 'wfh' ? 'It is on the calendar and the Executives know. No approval needed — you are working, just not at the office.' : form.kind === 'sick' ? (done && done.schedule && done.schedule.moved ? 'You’re moved to You Are Off on the schedule, and your PM and supervisor know you’re out. Feel better.' : 'The office has it. Feel better.') : 'It went to the office and management. You\'ll get a notice as soon as it\'s answered — and once it\'s approved it goes on the schedule.'}</div>
                <button onClick={() => { setDone(false); setForm({ start_date: '', end_date: '', reason: '', kind: 'vacation' }); }}
                  style={{ background: '#e87722', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                  Submit Another
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 6 }}>What kind</label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {KINDS.map(([k, l]) => (
                      <button key={k} type="button" onClick={() => setForm(f => ({ ...f, kind: k, end_date: instant(k) && !f.end_date ? f.start_date : f.end_date }))}
                        style={{ padding: '8px 13px', borderRadius: 999, border: `1.5px solid ${form.kind === k ? '#e87722' : '#e5e7eb'}`, background: form.kind === k ? '#fff7ed' : '#fff', color: form.kind === k ? '#9a3412' : '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{l}</button>
                    ))}
                  </div>
                  {form.kind === 'sick' && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>A sick day is recorded straight away — no approval needed. You’re moved to You Are Off, and your PM and supervisor are told you’re out.</div>}
                  {form.kind === 'wfh' && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>Noted straight away — no approval needed. It goes on the time-off calendar in its own colour and the Executives get an FYI. Nothing moves on the schedule; you are working.</div>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 6 }}>Start Date</label>
                    <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                      style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '11px 12px', fontSize: 15, color: '#111' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 6 }}>End Date</label>
                    <input type="date" value={form.end_date} min={form.start_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                      style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '11px 12px', fontSize: 15, color: '#111' }} />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 6 }}>Reason (optional)</label>
                  <textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} rows={3} placeholder="Vacation, personal, medical, etc."
                    style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '11px 12px', fontSize: 15, resize: 'none', fontFamily: 'inherit', color: '#111' }} />
                </div>
                {err && <div style={{ color: '#dc2626', fontSize: 13 }}>{err}</div>}
                <button onClick={submit} disabled={submitting || !form.start_date || !form.end_date}
                  style={{ width: '100%', padding: '14px 0', background: (!form.start_date || !form.end_date) ? '#e5e7eb' : '#e87722', color: (!form.start_date || !form.end_date) ? '#9ca3af' : '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                  {submitting ? 'Submitting…' : form.kind === 'sick' ? 'Log my sick day' : form.kind === 'wfh' ? 'Note I’m working from home' : 'Submit Request'}
                </button>
              </div>
            )
          )}

          {tab === 'history' && (
            myRequests.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0', fontSize: 14 }}>No requests yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {myRequests.map(r => (
                  <div key={r.id} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {({ sick: '🤒 ', vacation: '🌴 ', personal: '🏠 ', wfh: '🏡 ' })[r.kind] || ''}{r.start_date === r.end_date ? r.start_date : `${r.start_date} → ${r.end_date}`}
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '3px 8px', textTransform: 'uppercase', ...statusStyle(r.status) }}>{r.status}</span>
                    </div>
                    {r.reason && <div style={{ fontSize: 13, color: '#6b7280' }}>{r.reason}</div>}
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Submitted {new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
